import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Users, Clock, Briefcase, Building2, Camera, FileDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDateLong, fmtNum, fmtStamp } from "@/lib/format";
import { PageHeader, StatCard, LoadingBlock, StatusBadge, EmptyState } from "@/components/Shared";

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <LoadingBlock />;

  const gen = async () => {
    setBusy(true);
    try { await downloadFile("/exports/daily", { start: d.today, format: "pdf", photos: true }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fade-up">
      <PageHeader eyebrow={fmtDateLong(d.today)} title="Today in the field"
        actions={<>
          <button onClick={gen} disabled={busy} className="btn-brand h-11" data-testid="dashboard-generate-daily-button"><FileDown className="h-4 w-4" />{busy ? "Generating…" : "Generate Today's Report"}</button>
          <Link to="/admin/reports" className="btn-ghost h-11" data-testid="dashboard-all-reports-link">All reports</Link>
        </>} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6 stagger">
        <StatCard label="Reports today" value={d.reports_today} icon={FileText} testId="stat-reports-today" hint={d.drafts_today ? `${d.drafts_today} draft(s) in progress` : null} />
        <StatCard label="Employees reported" value={d.employees_today} icon={Users} testId="stat-employees-today" />
        <StatCard label="Hours today" value={fmtNum(d.hours_today)} icon={Clock} testId="stat-hours-today" />
        <StatCard label="Photos today" value={d.photos_today} icon={Camera} testId="stat-photos-today" />
        <StatCard label="Active projects" value={d.active_projects} icon={Briefcase} testId="stat-active-projects" />
        <StatCard label="Active clients" value={d.active_clients} icon={Building2} testId="stat-active-clients" />
      </div>

      {d.issues_today > 0 && (
        <div className="mt-5 flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900" data-testid="dashboard-issues-alert">
          <AlertTriangle className="h-5 w-5" /><p className="text-sm font-semibold">{d.issues_today} report(s) today flagged delays or issues.</p>
        </div>
      )}

      <section className="mt-8">
        <h2 className="mb-3 font-display text-2xl font-semibold">Today's reports</h2>
        {!d.reports.length ? (
          <EmptyState title="No reports submitted yet today" text="Submitted reports appear here as inspectors send them in." testId="dashboard-no-reports" />
        ) : (
          <div className="panel overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="dashboard-reports-table">
              <thead><tr className="border-b border-border bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">#</th><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Project</th>
                <th className="px-4 py-3 text-right">Hours</th><th className="px-4 py-3 text-right">Photos</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3">Status</th>
              </tr></thead>
              <tbody>
                {d.reports.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0 hover:bg-[#5C241E]/[0.03]">
                    <td className="px-4 py-3 font-mono-num">{r.report_number}</td>
                    <td className="px-4 py-3 font-semibold"><Link to={`/admin/reports/${r.id}`} className="hover:text-[#5C241E] hover:underline" data-testid={`dashboard-report-link-${r.id}`}>{r.employee_name}</Link>{r.has_issues && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-amber-600" />}</td>
                    <td className="px-4 py-3">{r.client_name}</td><td className="px-4 py-3">{r.project_name}</td>
                    <td className="px-4 py-3 text-right font-mono-num">{fmtNum(r.total_hours)}</td>
                    <td className="px-4 py-3 text-right font-mono-num">{r.photo_count}</td>
                    <td className="px-4 py-3 text-slate-500">{fmtStamp(r.submitted_at)}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
